import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux/es/hooks/useSelector";
import axios from "axios";
import { Box, Fade, Modal, Typography } from "@mui/material";

import { API_ENDPOINT } from "../../../services/api/resources";
import { shouldMakeAPICall } from "../../../offline";
import SearchBar from "../../../components/common/SearchBar/SearchBar";
import DButton from "../../../components/Dashboard/DashboardButtons/Dbutton";
import DataTable from "../../../components/common/DataTable/DataTableSorting";
import HeaderTitle from "../../../components/common/HeaderTitle/HeaderTitle";
import classes from "./RFQTab.module.css";
import plusIcon from "../../../assets/images/svg/plusIcon.png";

const FreeRFQ = () => {
  const navigate = useNavigate();
  const loginData = useSelector((state) => state.loginData.value);
  const buyerToken = loginData?.user?.token;
  const authToken = loginData?.auth_token;
  const mobileNumber=loginData?.user?.phone_number || "";

  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [freeRfqRows, setFreeRfqRows] = useState([]);
  const [selectedFreeRfq, setSelectedFreeRfq] = useState(null);
  const [vendorQuotations, setVendorQuotations] = useState([]);
  const [vendorQuotationLoading, setVendorQuotationLoading] = useState(false);
  const [expandedDescriptions, setExpandedDescriptions] = useState({});

  const formatDateTime = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString(undefined, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDateOnly = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };

  const formatTimeOnly = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  useEffect(() => {
    const load = async () => {
      if (!shouldMakeAPICall(authToken, buyerToken)) return;
      setLoading(true);
      try {
        const response = await axios({
          method: "POST",
          url: API_ENDPOINT.get_free_rfq_list,
          data: { user_token: buyerToken, page: 1, page_size: 20 },
          headers: { Authorization: `Bearer ${authToken}` },
        });

        const rows = Array.isArray(response?.data?.results) ? response.data.results : [];
        setFreeRfqRows(rows);
      } catch (error) {
        console.error("Error loading easyRFQ list:", error);
        setFreeRfqRows([]);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [authToken, buyerToken]);

  const filteredRows = useMemo(() => {
    const q = String(searchQuery || "").trim().toLowerCase();
    if (!q) return freeRfqRows;
    return freeRfqRows.filter((row) => {
      return [
        row?.rfq_code,
        row?.rfq_token,
        row?.bid_status,
        row?.created_date,
        row?.buyer_email,
        row?.buyer_company_name,
        row?.mobile_number,
        row?.vendor_emails?.join?.(", "),
        row?.purchase_request_number,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(q));
    });
  }, [freeRfqRows, searchQuery]);

  const headings = [
    {
      label: "RFQ ID",
      field: "rfq_code",
      col_width: "26rem",
      renderCell: (row) => (
        <div
          style={{
            maxWidth: "26rem",
            whiteSpace: "normal",
            wordBreak: "break-word",
            overflowWrap: "anywhere",
            lineHeight: 1.35,
          }}
        >
          {row?.rfq_code || "-"}
        </div>
      ),
    },
    // { label: "RFQ Token", field: "rfq_token" },
    // { label: "Buyer Company", field: "buyer_company_name" },
    // { label: "Buyer Email", field: "buyer_email" },
    {
      label: "Supplier Mobile Number",
      field: "vendor_mobile_numbers_text",
      col_width: "18rem",
      minWidth: "18rem",
      renderCell: (row) => (
        <div
          style={{
            maxWidth: "18rem",
            whiteSpace: "normal",
            wordBreak: "break-word",
            overflowWrap: "anywhere",
            lineHeight: 1.35,
          }}
        >
          {row?.vendor_mobile_numbers_text || "-"}
        </div>
      ),
    },
    {
      label: "Supplier Email(s)",
      field: "vendor_emails_text",
      col_width: "28rem",
      minWidth: "28rem",
      renderCell: (row) => (
        <div
          style={{
            maxWidth: "28rem",
            whiteSpace: "normal",
            wordBreak: "break-word",
            overflowWrap: "anywhere",
            lineHeight: 1.35,
          }}
        >
          {row?.vendor_emails_text || "-"}
        </div>
      ),
    },
    // { label: "Vendor Mobile(s)", field: "vendor_mobile_numbers_text" },
    // { label: "PR Number", field: "purchase_request_number" },
    {
      label: "Bid Start Date",
      field: "bid_from_date",
      col_width: "14rem",
      minWidth: "14rem",
      noWrap: true,
    },
    {
      label: "Bid End Date",
      field: "bid_to_date",
      col_width: "14rem",
      minWidth: "14rem",
      noWrap: true,
    },
    {
      label: "Bid Time",
      field: "bid_time",
      col_width: "12rem",
      minWidth: "12rem",
      noWrap: true,
    },
    {
      label: "Created Date",
      field: "created_date",
      col_width: "16rem",
      minWidth: "16rem",
      noWrap: true,
    },
    {
      label: "Status",
      field: "bid_status",
      col_width: "14rem",
      minWidth: "14rem",
      noWrap: true,
    },
    {
      label: "Items",
      field: "items_count",
      col_width: "10rem",
      minWidth: "10rem",
      noWrap: true,
    },
  ];

  const rows = filteredRows.map((row) => ({
    ...row,
    mobile_number: row?.mobile_number || row?.shipping_mobile_number || "-",
    buyer_email: row?.buyer_email || "-",
    buyer_company_name: row?.buyer_company_name || "-",
    vendor_emails_text: Array.isArray(row?.vendor_emails) ? row.vendor_emails.join(", ") : "-",
    vendor_mobile_numbers_text: Array.isArray(row?.vendor_mobile_numbers) ? row.vendor_mobile_numbers.join(", ") : "-",
    created_date: row?.created_date || row?.createdDate || "-",
    bid_from_date: formatDateOnly(row?.bid_from_date),
    bid_to_date: formatDateOnly(row?.bid_to_date),
    bid_time: formatTimeOnly(row?.bid_time),
    bid_status: row?.bid_status || "-",
    rfq_token: row?.rfq_token || "-",
    purchase_request_number: row?.purchase_request_number || "-",
    items_count: Array.isArray(row?.items) ? row.items.length : 0,
  }));

  const selectedItems = Array.isArray(selectedFreeRfq?.items) ? selectedFreeRfq.items : [];

  const formatPrice = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return "-";
    return num.toLocaleString(undefined, { maximumFractionDigits: 2 });
  };

  const isBlankValue = (value) => value === null || value === undefined || value === "";
  const toFiniteNumber = (value) => {
    const num = Number(String(value ?? "").replace(/,/g, "").trim());
    return Number.isFinite(num) ? num : null;
  };
  const getItemQuantity = (item = {}) => toFiniteNumber(item?.quantity) ?? 1;
  const getItemDiscount = (item = {}) => toFiniteNumber(item?.discount_amount ?? item?.discountAmount ?? item?.discount) ?? 0;
  const getItemTaxPercent = (item = {}) => toFiniteNumber(item?.tax ?? item?.tax_percent ?? item?.taxPercentage);
  const getItemUnitPrice = (item = {}) => {
    const unitPrice = item?.unit_price ?? item?.unitPrice;
    if (!isBlankValue(unitPrice)) return unitPrice;
    return item?.total_price ?? item?.totalPrice ?? item?.total_amount ?? item?.totalAmount;
  };
  const getItemTaxableAmount = (item = {}) => {
    const taxableAmount = item?.taxable_amount ?? item?.taxableAmount;
    if (!isBlankValue(taxableAmount)) return taxableAmount;
    const unitPrice = toFiniteNumber(getItemUnitPrice(item));
    if (unitPrice === null) return null;
    return Math.max(0, unitPrice * getItemQuantity(item) - getItemDiscount(item));
  };
  const getItemTotalAmount = (item = {}) => {
    const totalAmount = item?.total_price ?? item?.totalPrice ?? item?.total_amount ?? item?.totalAmount;
    if (!isBlankValue(totalAmount)) return totalAmount;
    const taxableAmount = toFiniteNumber(getItemTaxableAmount(item));
    if (taxableAmount === null) return null;
    const taxPercent = getItemTaxPercent(item) ?? 0;
    return taxableAmount + (taxableAmount * taxPercent) / 100;
  };
  const formatOptionalPrice = (value) => (isBlankValue(value) ? "-" : formatPrice(value));
  const formatTaxPercent = (value) => {
    const tax = toFiniteNumber(value);
    return tax === null ? "-" : `${formatPrice(tax)}%`;
  };

  const toggleDescription = (key) => {
    setExpandedDescriptions((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const renderExpandableText = (value, key, maxLength = 80) => {
    const text = String(value || "-");
    const isExpanded = Boolean(expandedDescriptions[key]);
    const shouldTruncate = text.length > maxLength;
    const shownText = !shouldTruncate || isExpanded ? text : `${text.slice(0, maxLength)}...`;
    return (
      <div title={text} style={{ maxWidth: 260, whiteSpace: isExpanded ? "normal" : "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
        <span>{shownText}</span>
        {shouldTruncate && (
          <button
            type="button"
            onClick={() => toggleDescription(key)}
            style={{ display: "block", marginTop: 6, padding: 0, border: 0, background: "transparent", color: "#2563eb", cursor: "pointer", fontSize: 12 }}
          >
            {isExpanded ? "Read less" : "Read more"}
          </button>
        )}
      </div>
    );
  };

  const handleViewFreeRfq = async (row) => {
    const rfqToken = row?.rfq_token || "";
    if (!rfqToken) return;
    navigate(`/Buyer_dashboard/FreeRFQ/view`, {
      state: {
        freeRfq: row,
      },
    });
  };

  return (
    <div className={classes.users_tab_wrapper}>
      <div className={classes.users_topHeader}>
        <div className={classes.lhs} style={{ marginLeft: "1.5rem" }}>
          <HeaderTitle titleText="easyRFQ" customclass={classes.dashHead} />
        </div>
      </div>

      <div className={`${classes.search_add_wrapper} ${classes.edgeActionBar}`} style={{ marginLeft: "1.5rem" }}>
        <div className={`${classes.flex} ${classes.flex_align_end} ${classes.search_add_wrapper_content_left}`}>
          <SearchBar
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search easyRFQ"
          />
        </div>

        <div className={`${classes.rhsButtons} ${classes.rightAlignedAction}`}>
          <DButton
            labelImg={plusIcon}
            iconWidth="1rem"
            iconHeight="1rem"
            label={"CREATE easyRFQ"}
            btnHeight="4rem"
            buyerBtn={true}
            onClick={() => navigate("/Buyer_dashboard/FreeRFQ/create")}
          />
        </div>
      </div>

      <div className={`${classes.d_dataTable} `}>
        <DataTable
          headings={headings}
          tableData={rows}
          tableSize={"170rem"}
          stickyFirstColumn={true}
          fixedLayout={true}
          actions={true}
          viewBtn={true}
          viewTooltip="View"
          viewClick={(_, row) => handleViewFreeRfq(row)}
          isLoading={loading}
          errorMessage={rows?.length === 0 ? "No easyRFQ found." : ""}
          sorting={true}
        />
      </div>

      <Modal
        open={Boolean(selectedFreeRfq)}
        onClose={() => setSelectedFreeRfq(null)}
        closeAfterTransition
      >
        <Fade in={Boolean(selectedFreeRfq)}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "min(96vw, 1200px)",
              maxHeight: "90vh",
              overflow: "auto",
              bgcolor: "#fff",
              borderRadius: "16px",
              boxShadow: 24,
              p: 2.5,
            }}
          >
            <Typography sx={{ fontFamily: "gilroyBold", fontSize: 20, mb: 1 }}>
              easyRFQ Details
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                gap: 1.5,
                mb: 2,
                "@media (max-width: 900px)": { gridTemplateColumns: "1fr" },
              }}
            >
              {[
                ["RFQ ID", selectedFreeRfq?.rfq_code],
                ["Bid Start Date", selectedFreeRfq?.bid_from_date],
                ["Bid End Date", selectedFreeRfq?.bid_to_date],
                ["Bid Time", selectedFreeRfq?.bid_time],
                ["Bid Status", selectedFreeRfq?.bid_status],
                // ["Mobile Number", selectedFreeRfq?.mobile_number],
                ["Shipping Address 1", selectedFreeRfq?.shipping_address1],
                ["Shipping Address 2", selectedFreeRfq?.shipping_address2],
                ["City", selectedFreeRfq?.city],
                ["State", selectedFreeRfq?.state],
              ].map(([label, value]) => (
                <Box
                  key={label}
                  sx={{
                    border: "1px solid #e2e8f0",
                    borderRadius: "12px",
                    p: 1.25,
                    background: "#f8fafc",
                  }}
                >
                  <Typography sx={{fontSize: 12, color: "#64748b" }}>{label}</Typography>
                  <Typography sx={{ fontFamily: "gilroyMedium", fontSize: 14, wordBreak: "break-word" }}>
                    {String(value ?? "-")}
                  </Typography>
                </Box>
                ))}
            </Box>

            <Typography sx={{ fontFamily: "gilroyBold", fontSize: 16, mb: 1 }}>
              RFQ Items
            </Typography>
            <div style={{ overflowX: "auto", border: "1px solid #e2e8f0", borderRadius: 12, background: "#fff", marginBottom: 16 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 950 }}>
                <thead style={{ background: "#e2e8f0" }}>
                  <tr>
                    {[
                      "Item Name",
                      "Item Code",
                      "HSN",
                      "Quantity",
                      "Unit",
                      "Description",
                      "Expense Type",
                      "Unit Price",
                      "Total Price",
                    ].map((heading) => (
                      <th key={heading} style={{ padding: 10, textAlign: "left", fontSize: 14 }}>
                        {heading}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Array.isArray(selectedFreeRfq?.items) && selectedFreeRfq.items.length > 0 ? (
                    selectedFreeRfq.items.map((item, idx) => (
                      <tr key={`${item?.itemCode || item?.itemName || "item"}-${idx}`}>
                        <td style={{ padding: 10, borderTop: "1px solid #e2e8f0" }}>{item?.itemName || "-"}</td>
                        <td style={{ padding: 10, borderTop: "1px solid #e2e8f0" }}>{item?.itemCode || "-"}</td>
                        <td style={{ padding: 10, borderTop: "1px solid #e2e8f0" }}>{item?.hsnCode || "-"}</td>
                        <td style={{ padding: 10, borderTop: "1px solid #e2e8f0" }}>{item?.quantity ?? "-"}</td>
                        <td style={{ padding: 10, borderTop: "1px solid #e2e8f0" }}>{item?.unit || "-"}</td>
                        <td style={{ padding: 10, borderTop: "1px solid #e2e8f0" }}>{item?.description || "-"}</td>
                        <td style={{ padding: 10, borderTop: "1px solid #e2e8f0" }}>{item?.expense_type || "-"}</td>
                        <td style={{ padding: 10, borderTop: "1px solid #e2e8f0" }}>{item?.unitPrice ?? "-"}</td>
                        <td style={{ padding: 10, borderTop: "1px solid #e2e8f0" }}>{item?.totalPrice ?? "-"}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={9} style={{ padding: 14, textAlign: "center" }}>
                        No RFQ items found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <Typography sx={{ fontFamily: "gilroyBold", fontSize: 16, mb: 1 }}>
              Supplier Bid Details
            </Typography>
            {vendorQuotationLoading ? (
              <Typography sx={{ fontSize: 14, color: "#64748b" }}>Loading vendor quotations...</Typography>
            ) : vendorQuotations.length > 0 ? (
              vendorQuotations.map((quote, idx) => {
                const quoteItems = Array.isArray(quote?.quotation_details?.items) ? quote.quotation_details.items : [];
                return (
                  <Box
                    key={`${quote?.vendor_quotation_token || "quote"}-${idx}`}
                    sx={{
                      mb: 2,
                      p: 1.5,
                      border: "1px solid #e2e8f0",
                      borderRadius: "12px",
                      background: "#f8fafc",
                    }}
                  >
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                        gap: 1.25,
                        mb: 1.5,
                        "@media (max-width: 900px)": { gridTemplateColumns: "1fr" },
                      }}
                    >
                      {[
                        ["Supplier Email", quote?.vendor_email],
                        ["Supplier Mobile", quote?.vendor_mobile],
                        // ["File Name", quote?.file_name],
                        // ["Pages", quote?.doc_page_count],
                        // ["Created At", quote?.created_at],
                        ["Updated At", quote?.updated_at],
                      ].map(([label, value]) => (
                        <Box
                          key={label}
                          sx={{
                            border: "1px solid #e2e8f0",
                            borderRadius: "10px",
                            p: 1.1,
                            background: "#fff",
                          }}
                        >
                          <Typography sx={{ fontSize: 12, color: "#64748b" }}>{label}</Typography>
                          <Typography sx={{ fontFamily: "gilroyMedium", fontSize: 14 }}>
                            {String(value ?? "-")}
                          </Typography>
                        </Box>
                      ))}
                    </Box>

                    <Typography sx={{ fontFamily: "gilroyBold", fontSize: 14, mb: 1 }}>
                      Extracted RFQ Items
                    </Typography>
                    <div style={{ overflowX: "auto", border: "1px solid #e2e8f0", borderRadius: 12, background: "#fff" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1180 }}>
                        <thead style={{ background: "#e2e8f0" }}>
                          <tr >
                            {["Item Name", "Actual Item Name", "Part No","Item Code", "Quantity", "Unit Price", "Discount", "Tax", "Taxable Amount", "Total Amount", "Description"].map(
                              (heading) => (
                                <th key={heading} style={{ padding: 10, textAlign: "left", fontSize: 13, whiteSpace: "nowrap" }}>
                                  {heading}
                                </th>
                              ),
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {quoteItems.length > 0 ? (
                            quoteItems.map((item, itemIdx) => (
                              <tr key={`${item?.item_code || item?.itemName || "item"}-${itemIdx}`}>
                                <td style={{ padding: 10, borderTop: "1px solid #e2e8f0" }}>{renderExpandableText(item?.item_name || item?.itemName, `item-name-${itemIdx}`)}</td>
                                <td style={{ padding: 10, borderTop: "1px solid #e2e8f0" }}>{renderExpandableText(item?.original_item_name, `actual-name-${itemIdx}`)}</td>
                                <td title={item?.part_number || ""} style={{ padding: 10, borderTop: "1px solid #e2e8f0", whiteSpace: "nowrap" }}>{item?.part_number || "-"}</td>
                                <td title={item?.item_code || item?.itemCode || ""} style={{ padding: 10, borderTop: "1px solid #e2e8f0", whiteSpace: "nowrap" }}>{item?.item_code || item?.itemCode || "-"}</td>
                                <td style={{ padding: 10, borderTop: "1px solid #e2e8f0", whiteSpace: "nowrap" }}>{item?.quantity ?? "-"}</td>
                                <td style={{ padding: 10, borderTop: "1px solid #e2e8f0", whiteSpace: "nowrap" }}>{formatOptionalPrice(getItemUnitPrice(item))}</td>
                                <td style={{ padding: 10, borderTop: "1px solid #e2e8f0", whiteSpace: "nowrap" }}>{formatOptionalPrice(item?.discount_amount)}</td>
                                <td style={{ padding: 10, borderTop: "1px solid #e2e8f0", whiteSpace: "nowrap" }}>{formatTaxPercent(getItemTaxPercent(item))}</td>
                                <td style={{ padding: 10, borderTop: "1px solid #e2e8f0", whiteSpace: "nowrap" }}>{formatOptionalPrice(getItemTaxableAmount(item))}</td>
                                <td style={{ padding: 10, borderTop: "1px solid #e2e8f0", whiteSpace: "nowrap" }}>{formatOptionalPrice(getItemTotalAmount(item))}</td>
                                <td style={{ padding: 10, borderTop: "1px solid #e2e8f0", maxWidth: 320 }}>
                                  {renderExpandableText(item?.description, `${item?.item_code || item?.itemName || "item"}-${itemIdx}`, 90)}
                                </td>
                                
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={11} style={{ padding: 14, textAlign: "center" }}>
                                No quotation items found.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </Box>
                );
              })
            ) : (
              <Typography sx={{ fontSize: 14, color: "#64748b" }}>No vendor quotations found.</Typography>
            )}

            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
              <DButton label="Close" btnHeight="4rem" buyerBtn={true} onClick={() => setSelectedFreeRfq(null)} />
            </Box>
          </Box>
        </Fade>
      </Modal>
    </div>
  );
};

export default FreeRFQ;
